<template>
  <div v-if="canView" class="bg-white border border-gray-200 rounded-lg">
    <!-- Header: title + actions on top, filters on their own row. -->
    <div class="border-b border-gray-200">
      <div class="flex items-center justify-between px-6 py-3">
        <div class="flex items-center gap-2">
          <span class="text-xs font-semibold text-gray-900">Performance Signal Study</span>
          <span class="text-[10px] text-gray-400 tabular-nums">{{ filtered.length }} constraints</span>
        </div>
        <div class="flex items-center gap-2">
          <button
            v-if="hasFilters"
            class="text-[10px] text-gray-400 hover:text-gray-600"
            @click="clearFilters"
          >Clear filters</button>
          <ExportMenu size="sm" :disabled="loading || !filtered.length" :exports="[
            { type: 'csv', action: exportCsv },
            { type: 'excel', action: exportXlsx },
          ]" />
        </div>
      </div>
      <div class="flex flex-wrap items-center gap-2 px-6 pb-3">
        <select v-model="filterPractice" class="sensitive text-xs border border-gray-300 rounded px-2 py-1 text-gray-700 max-w-[16rem]">
          <option :value="null">All practices</option>
          <option v-for="n in practiceOptions" :key="n" :value="n">{{ titleCase(n) }}</option>
        </select>
        <select v-model="filterPtm" class="text-xs border border-gray-300 rounded px-2 py-1 text-gray-700">
          <option :value="null">All PTMs</option>
          <option v-for="n in ptmOptions" :key="n" :value="n">{{ n }}</option>
        </select>
        <select v-model="filterYear" class="text-xs border border-gray-300 rounded px-2 py-1 text-gray-700">
          <option :value="null">All years</option>
          <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
        </select>
        <select v-model="filterInitiative" class="text-xs border border-gray-300 rounded px-2 py-1 text-gray-700">
          <option :value="null">All initiatives</option>
          <option value="awv">AWV</option>
          <option value="hcc">HCC</option>
        </select>
        <select v-model="filterType" class="text-xs border border-gray-300 rounded px-2 py-1 text-gray-700">
          <option :value="null">All types</option>
          <option v-for="t in typeOptions" :key="t" :value="t">{{ t }}</option>
        </select>
        <select v-model="filterStatus" class="text-xs border border-gray-300 rounded px-2 py-1 text-gray-700">
          <option :value="null">All statuses</option>
          <option value="resolved">Resolved</option>
          <option value="in_progress">In progress</option>
          <option value="open">Open</option>
          <option value="not_removable">Not removable</option>
        </select>
        <input
          v-model="search"
          type="text"
          placeholder="Search..."
          class="text-xs border border-gray-300 rounded px-2 py-1 text-gray-700 w-48 ml-auto"
        >
      </div>
    </div>

    <div v-if="loading" class="flex items-center justify-center py-12">
      <LoadingIndicator size="md" />
    </div>
    <div v-else-if="error" class="px-6 py-12 text-center text-xs text-red-500">{{ error }}</div>
    <div v-else-if="!gaps.length" class="px-6 py-12 text-center text-xs text-gray-400">
      No constraints extracted yet.
    </div>

    <!-- What the study found, and how. Sits above the operational view so the numbers
         in the table are read against the estimate they came from. -->
    <div v-else class="px-6 py-4 border-b border-gray-200">
      <div class="flex items-start justify-between gap-6">
        <div class="grid grid-cols-2 gap-4 flex-1">
          <div
            v-for="e in STUDY"
            :key="e.key"
            class="border border-gray-200 rounded-lg px-4 py-3"
          >
            <div class="flex items-baseline gap-2 mb-1">
              <span
                class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium"
                :class="INITIATIVE_CLS[e.key]"
              >{{ e.key.toUpperCase() }}</span>
              <span class="text-sm font-semibold text-gray-900 tabular-nums">+{{ e.effect }}pp</span>
              <span class="text-[10px] text-gray-400">per constraint closed</span>
              <span
                class="text-[10px] ml-auto tabular-nums"
                :class="e.p < 0.01 ? 'text-green-700' : 'text-amber-700'"
              >p={{ e.p }}</span>
            </div>
            <div class="text-[11px] text-gray-600 leading-snug">{{ e.reading }}</div>
            <div class="text-[10px] text-gray-400 mt-1.5">{{ e.spec }}</div>
          </div>
        </div>
        <button
          class="text-[10px] text-gray-400 hover:text-gray-600 shrink-0 mt-1"
          @click="showMethod = !showMethod"
        >{{ showMethod ? 'Hide' : 'Method' }}</button>
      </div>

      <div v-if="showMethod" class="mt-3 text-[11px] text-gray-600 leading-relaxed space-y-2 border-t border-gray-100 pt-3">
        <p>
          An LLM read every practice-liaison meeting of 2025 — 3,257 transcripts across
          328 practices — and recorded, per named provider, what was constraining their
          work and whether it was ever resolved. That is the exposure; the outcome is
          that provider's own claims either side of the resolution date.
        </p>
        <p>
          <strong>AWV</strong> is measured directly: AWVs per patient seen in the 13 weeks
          before versus after. <strong>HCC</strong> cannot be, because recapture is a
          cumulative within-year rate that only ever rises — so each provider's trajectory
          is forecast from pre-resolution data alone and compared to what they actually
          did. Unresolved constraints have no resolution date, so they have no before or
          after and are left blank here.
        </p>
        <p>
          <strong>TOC was tested and dropped.</strong> A provider has a median of four
          discharges in a 13-week window, so almost all the variation in the outcome is
          sampling noise rather than provider behaviour. No effect is estimable and none
          is shown.
        </p>
        <p class="text-gray-400">
          Constraints are attributed to the clinician whose work is blocked, not whoever
          spoke. Estimates are associational — resolution is not randomly assigned, and
          practices that get constraints closed may differ from those that don't. The
          within-provider AWV comparison (a provider's resolved constraints against their
          own unresolved ones, 52 providers, +2.58pp p=0.020) is the strongest guard
          against that.
        </p>
      </div>
    </div>

    <!-- When constraints were raised and when they were resolved. Bars are the monthly
         counts; the line is the running count of addressable constraints still open.
         Respects the filters, unlike the PTM summary below. -->
    <div v-if="!loading && !error && trend.length" class="px-6 pt-4 pb-2 border-b border-gray-200">
      <div class="flex items-baseline gap-4">
        <span class="text-[10px] text-gray-400 uppercase tracking-wider">Raised and resolved over time</span>
        <span class="text-[10px] text-gray-400 ml-auto tabular-nums">
          {{ trendTotals.raised }} raised
          <span class="text-gray-500">({{ trendTotals.blocked }} not solvable by a PTM)</span>
          · {{ trendTotals.resolved }} resolved · {{ trendTotals.open }} still open
        </span>
      </div>
      <Chart_V5
        :chart_data="trendChart"
        :chart_config="trendConfig"
        override-height="h-[260px]"
      />
    </div>

    <!-- Closure by PTM. Bar is closed / (closed + open); not-removable constraints are
         excluded from the rate since nobody can act on them. Click to filter. -->
    <div v-if="!loading && !error && ptmSummary.length" class="px-6 py-3 border-b border-gray-200 bg-gray-50">
      <div class="grid grid-cols-3 gap-x-6 gap-y-2">
        <button
          v-for="r in ptmSummary"
          :key="r.ptm"
          class="text-left group"
          :class="filterPtm === r.ptm ? 'opacity-100' : 'opacity-80 hover:opacity-100'"
          @click="filterPtm = filterPtm === r.ptm ? null : r.ptm"
        >
          <div class="flex items-baseline gap-2">
            <span
              class="sensitive text-xs truncate"
              :class="filterPtm === r.ptm ? 'font-semibold text-gray-900' : 'text-gray-700'"
            >{{ r.ptm }}</span>
            <span class="text-[10px] text-gray-400 tabular-nums ml-auto shrink-0">
              {{ r.closed }}/{{ r.closed + r.open }}
            </span>
            <span class="text-xs font-semibold text-gray-900 tabular-nums shrink-0 w-9 text-right">
              {{ r.rate }}%
            </span>
          </div>
          <div class="flex h-1.5 rounded-sm overflow-hidden bg-gray-200 mt-1">
            <div class="bg-green-500" :style="{ width: `${r.rate}%` }"></div>
          </div>
        </button>
      </div>
      <div class="text-[10px] text-gray-400 mt-2">
        Closed / addressable constraints. Excludes not removable.
      </div>
    </div>

    <div v-if="!loading && !error && gaps.length" class="overflow-auto" style="max-height: 700px;">
      <table class="w-full text-xs">
        <thead class="sticky top-0 bg-gray-50 z-10">
          <tr class="text-gray-400 text-[10px] uppercase tracking-wider border-b border-gray-200">
            <th
              v-for="c in columns"
              :key="c.key"
              class="font-medium px-3 py-2 cursor-pointer hover:text-gray-600 whitespace-nowrap"
              :class="c.align === 'right' ? 'text-right' : 'text-left'"
              @click="sortBy(c.key)"
            >
              {{ c.label }}
              <span v-if="sortKey === c.key" class="text-gray-600">{{ sortDesc ? '↓' : '↑' }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="g in filtered"
            :key="`${g.study_year}-${g.entity_id}-${g.gap_ix}`"
            class="border-b border-gray-100 cursor-pointer"
            :class="isSelected(g) ? 'bg-blue-50' : 'hover:bg-gray-50'"
            @click="openDrawer(g)"
          >
            <td class="px-3 py-1.5 text-gray-400 tabular-nums">{{ g.study_year }}</td>
            <td class="sensitive px-3 py-1.5 text-gray-900">{{ titleCase(g.practice_name) }}</td>
            <td class="sensitive px-3 py-1.5 text-gray-700">{{ titleCase(g.provider_name) }}</td>
            <td class="px-3 py-1.5 text-gray-400 tabular-nums">{{ g.npi }}</td>
            <td class="sensitive px-3 py-1.5 text-gray-700">{{ g.ptm_name || '—' }}</td>
            <td class="px-3 py-1.5">
              <span
                class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium"
                :class="INITIATIVE_CLS[g.initiative]"
              >{{ g.initiative.toUpperCase() }}</span>
            </td>
            <td class="px-3 py-1.5">
              <span
                class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap"
                :class="TYPE_CLS[g.input_type]"
              >{{ g.input_type }}</span>
            </td>
            <td class="px-3 py-1.5 text-gray-700">{{ g.title }}</td>
            <td class="px-3 py-1.5">
              <span
                class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap"
                :class="statusCls(g)"
              >{{ statusLabel(g) }}</span>
            </td>
            <td class="px-3 py-1.5 text-gray-400 tabular-nums whitespace-nowrap">{{ g.supplied_date || '—' }}</td>
            <td class="px-3 py-1.5 text-right tabular-nums text-gray-700">{{ fmt(g.pre_value) }}</td>
            <td class="px-3 py-1.5 text-right tabular-nums text-gray-700">{{ fmt(g.post_value) }}</td>
            <td
              class="px-3 py-1.5 text-right tabular-nums font-semibold"
              :class="g.delta == null ? 'text-gray-300' : g.delta >= 0 ? 'text-green-700' : 'text-red-700'"
            >{{ g.delta == null ? '—' : `${g.delta >= 0 ? '+' : ''}${g.delta.toFixed(1)}` }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Detail drawer: the full constraint, what was done about it, and the verbatim
         quotes behind both. Resolution evidence is separated from the evidence for the
         constraint itself. -->
    <div v-if="selected" class="fixed inset-0 z-50 flex justify-end" @click.self="selected = null">
      <div class="bg-black/25 absolute inset-0" @click="selected = null"></div>
      <div class="relative w-[620px] h-full bg-white shadow-xl overflow-y-auto">
        <div class="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex items-start justify-between z-10">
          <div class="min-w-0">
            <div class="flex items-center gap-1.5 mb-0.5">
              <span
                class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium"
                :class="INITIATIVE_CLS[selected.initiative]"
              >{{ selected.initiative.toUpperCase() }}</span>
              <span
                class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium"
                :class="statusCls(selected)"
              >{{ statusLabel(selected) }}</span>
              <span class="text-[10px] text-gray-400">{{ selected.input_type }}</span>
            </div>
            <div class="text-xs font-semibold text-gray-900">{{ selected.title }}</div>
          </div>
          <button class="text-gray-400 hover:text-gray-600 ml-4 shrink-0" @click="selected = null">&times;</button>
        </div>

        <div class="px-5 py-4 space-y-4">
          <p v-if="selected.summary" class="text-xs text-gray-700 leading-relaxed">{{ selected.summary }}</p>

          <div class="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
            <div>
              <div class="text-[10px] text-gray-400 uppercase">Practice</div>
              <div class="sensitive text-gray-900">{{ titleCase(selected.practice_name) }}</div>
            </div>
            <div>
              <div class="text-[10px] text-gray-400 uppercase">Provider</div>
              <div class="sensitive text-gray-900">
                {{ titleCase(selected.provider_name) }}
                <span class="text-gray-400 tabular-nums">· {{ selected.npi }}</span>
              </div>
            </div>
            <div>
              <div class="text-[10px] text-gray-400 uppercase">PTM</div>
              <div class="sensitive text-gray-900">{{ selected.ptm_name || '—' }}</div>
            </div>
            <div>
              <div class="text-[10px] text-gray-400 uppercase">First raised</div>
              <div class="text-gray-900 tabular-nums">{{ selected.first_stated_date || '—' }}</div>
            </div>
          </div>

          <div
v-if="!selected.removable && selected.not_removable_reason"
               class="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
            Not removable — {{ selected.not_removable_reason }}
          </div>

          <!-- What was done -->
          <div>
            <div class="text-[10px] text-gray-400 uppercase mb-1">What was done</div>
            <div v-if="!selected.n_interventions" class="text-xs text-gray-400">
              Nothing recorded.
            </div>
            <div v-else class="text-xs text-gray-700">
              {{ selected.n_interventions }} intervention{{ selected.n_interventions === 1 ? '' : 's' }}
              <span v-if="selected.has_vytalize_intervention" class="text-gray-400">· Vytalize acted</span>
              <span v-if="selected.has_practice_intervention" class="text-gray-400">· practice acted</span>
            </div>
          </div>

          <!-- Outcome -->
          <div v-if="selected.supplied">
            <div class="text-[10px] text-gray-400 uppercase mb-1">
              Resolved {{ selected.supplied_date }}
            </div>
            <div v-if="selected.delta != null" class="flex gap-6">
              <div>
                <div class="text-[10px] text-gray-400">{{ selected.initiative === 'hcc' ? 'Forecast' : 'Before' }}</div>
                <div class="text-xs font-semibold text-gray-900 tabular-nums">{{ fmt(selected.pre_value) }}</div>
              </div>
              <div>
                <div class="text-[10px] text-gray-400">{{ selected.initiative === 'hcc' ? 'Observed' : 'After' }}</div>
                <div class="text-xs font-semibold text-gray-900 tabular-nums">{{ fmt(selected.post_value) }}</div>
              </div>
              <div>
                <div class="text-[10px] text-gray-400">Change</div>
                <div
                  class="text-xs font-semibold tabular-nums"
                  :class="selected.delta >= 0 ? 'text-green-700' : 'text-red-700'"
                >{{ selected.delta >= 0 ? '+' : '' }}{{ selected.delta.toFixed(1) }}</div>
              </div>
              <div v-if="selected.initiative === 'hcc'" class="text-[10px] text-gray-400 self-end pb-0.5">
                13wk forecast from the provider's pre-resolution trajectory
              </div>
            </div>
            <div v-else class="text-xs text-gray-400">
              No claims window available for this resolution date.
            </div>
          </div>

          <!-- Evidence -->
          <div v-for="node in ['gap', 'resolution']" :key="node">
            <template v-if="evidenceFor(node).length">
              <div class="text-[10px] text-gray-400 uppercase mb-1">
                {{ node === 'resolution' ? 'Resolution evidence' : 'Evidence for the constraint' }}
              </div>
              <div
                v-for="(e, i) in evidenceFor(node)"
                :key="i"
                class="text-xs text-gray-600 pl-3 border-l-2 border-gray-200 mb-2"
              >
                "{{ e.quote }}"
                <span class="sensitive block text-[10px] text-gray-400 mt-0.5">
                  {{ e.speaker }} · {{ e.meeting_date }} ·
                  {{ e.source === 'liaison_statement' ? 'liaison' : 'practice' }}
                </span>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import LoadingIndicator from '../widgetcomponents/library/LoadingIndicator.vue';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import Chart_V5 from '../widgetcomponents/Viz_Elements/Chart_V5.vue';
import { exportToExcel } from '@/utils/exportArraytoXlsx';

const UNASSIGNED = '(unassigned)';

// Headline results. Kept here rather than fetched: they are a property of the study
// as run, not of the rows currently on screen.
const STUDY = [
  {
    key: 'awv', effect: 3.26, p: 0.003,
    reading: 'Providers whose constraint was resolved went from 14.2 to 18.1 AWVs '
      + 'per 100 patients seen; those still blocked went 13.7 to 14.6.',
    spec: 'delta ~ supplied + pre_rate + month FE · WLS · cluster on NPI · n=476, 90 closed',
  },
  {
    key: 'hcc', effect: 0.62, p: 0.021,
    reading: 'Resolved providers beat their own forecast by 0.58pp; unresolved '
      + 'landed on it (−0.14pp), which is what an unbiased forecast should do.',
    spec: 'surprise ~ supplied + y_pre + month FE · OLS · cluster on NPI · n=551, 133 closed',
  },
];

const INITIATIVE_CLS = {
  awv: 'bg-blue-100 text-blue-700',
  hcc: 'bg-purple-100 text-purple-700',
};

// What is constraining the provider: they don't know how, they lack people or time,
// nobody on the team owns it, or they don't think it is worth doing.
const TYPE_CLS = {
  knowledge: 'bg-sky-50 text-sky-700',
  capacity: 'bg-orange-50 text-orange-700',
  ownership: 'bg-violet-50 text-violet-700',
  motivation: 'bg-rose-50 text-rose-700',
};

const COLUMNS = [
  { key: 'study_year', label: 'Year' },
  { key: 'practice_name', label: 'Practice' },
  { key: 'provider_name', label: 'Provider' },
  { key: 'npi', label: 'NPI' },
  { key: 'ptm_name', label: 'PTM' },
  { key: 'initiative', label: 'Initiative' },
  { key: 'input_type', label: 'Type' },
  { key: 'title', label: 'Constraint' },
  { key: 'status', label: 'Status' },
  { key: 'supplied_date', label: 'Resolved' },
  { key: 'pre_value', label: 'Before', align: 'right' },
  { key: 'post_value', label: 'After', align: 'right' },
  { key: 'delta', label: 'Change', align: 'right' },
];

export default {
  name: 'PerformanceSignalStudy',
  components: { LoadingIndicator, ExportMenu, Chart_V5 },
  props: {
    // Supplied automatically by InsightsNew_MetricView's pageContext.
    year: { type: Number, default: null },
    entityId: { type: Number, default: null },
    metricId: { type: Number, default: null },
    metricIds: { type: Array, default: () => [] },
    performanceDomainId: { type: Number, default: null },
    isDomain: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: true,
      error: null,
      gaps: [],
      search: '',
      filterInitiative: null,
      filterYear: null,
      filterPractice: null,
      filterPtm: null,
      filterType: null,
      filterStatus: null,
      selected: null,
      showMethod: false,
      STUDY,
      sortKey: 'practice_name',
      sortDesc: false,
      columns: COLUMNS,
      INITIATIVE_CLS,
      TYPE_CLS,
    };
  },
  computed: {
    canView() {
      return (
        this.$store.getters['auth/current_user']?.is_superuser ||
        this.$store.getters['tenant/isTenantAdmin'] ||
        false
      );
    },
    practiceOptions() {
      return [...new Set(this.gaps.map(g => g.practice_name).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b));
    },
    hasFilters() {
      return !!(this.filterPractice || this.filterPtm || this.filterInitiative
        || this.filterType || this.filterStatus || this.search || this.filterYear);
    },
    yearOptions() {
      return [...new Set(this.gaps.map(g => g.study_year).filter(Boolean))].sort();
    },
    typeOptions() {
      return [...new Set(this.gaps.map(g => g.input_type).filter(Boolean))].sort();
    },
    ptmOptions() {
      return [...new Set(this.gaps.map(g => g.ptm_name || UNASSIGNED))].sort();
    },
    // Closure rate per PTM. Ignores the initiative/status/search filters on purpose --
    // it is the standing picture of who is closing what, not a view of the filtered set.
    ptmSummary() {
      const by = {};
      for (const g of this.gaps) {
        const k = g.ptm_name || UNASSIGNED;
        const r = (by[k] = by[k] || { ptm: k, closed: 0, open: 0, na: 0 });
        if (!g.removable) r.na += 1;
        else if (g.supplied) r.closed += 1;
        else r.open += 1;
      }
      return Object.values(by)
        .map(r => ({
          ...r,
          rate: r.closed + r.open ? Math.round((100 * r.closed) / (r.closed + r.open)) : 0,
        }))
        .sort((a, b) => b.rate - a.rate || b.closed + b.open - (a.closed + a.open));
    },
    // Monthly raised / resolved, plus the running count of addressable constraints not
    // yet closed. Built from the FILTERED set so it answers the question actually on
    // screen. A constraint counts as raised in the month it was first stated and
    // resolved in the month it was supplied -- those are different months for most of
    // them (median 45-54 days), which is the whole point of plotting both.
    trend() {
      const by = {};
      const touch = (k) => (by[k] = by[k]
        || { month: k, raised: 0, addressable: 0, blocked: 0, resolved: 0, closed: 0 });
      for (const g of this.filtered) {
        const rm = (g.first_stated_date || '').slice(0, 7);
        if (rm) {
          touch(rm).raised += 1;
          // A NOT-REMOVABLE constraint is one no liaison visit can fix -- the 365-day
          // AWV lockout, a specialist who already billed it, an FQHC that cannot bill
          // an office visit and a wellness visit the same day. It is split out of the
          // raised bar and excluded from the open line, because counting it as backlog
          // shows work nobody can do and makes closure look worse than it is.
          if (g.removable) by[rm].addressable += 1;
          else by[rm].blocked += 1;
        }
        const sm = (g.supplied && g.supplied_date || '').slice(0, 7);
        if (sm) {
          touch(sm).resolved += 1;
          if (g.removable) touch(sm).closed += 1;
        }
      }
      const months = Object.keys(by).sort();
      if (!months.length) return [];

      // Fill the gaps so a quiet month renders as zero rather than collapsing the axis.
      const out = [];
      const [y0, m0] = months[0].split('-').map(Number);
      const [y1, m1] = months[months.length - 1].split('-').map(Number);
      let running = 0;
      for (let y = y0, m = m0; y < y1 || (y === y1 && m <= m1); m === 12 ? (m = 1, y += 1) : (m += 1)) {
        const k = `${y}-${String(m).padStart(2, '0')}`;
        const r = by[k] || { raised: 0, addressable: 0, blocked: 0, resolved: 0, closed: 0 };
        running += (r.addressable || 0) - (r.closed || 0);
        const d = new Date(y, m - 1, 1);
        out.push({
          month: k,
          raised: r.raised,
          addressable: r.addressable || 0,
          blocked: r.blocked || 0,
          resolved: r.resolved,
          open: running,
          label: d.toLocaleString('en-US', { month: 'short', year: '2-digit' }),
        });
      }
      return out;
    },
    // Chart_V5 payload. Raised/resolved are monthly FLOWS and share the left axis;
    // "still open" is a STOCK that runs an order of magnitude higher, so it takes the
    // right axis -- on a shared axis it flattens both bar series to nothing.
    trendChart() {
      const t = this.trend;
      return {
        labels: t.map(m => m.label),
        series: [
          // Raised splits into two STACKED segments at the same offset: the part a
          // liaison can actually act on, and the part no visit can fix. Same total
          // bar height as before, so the comparison against Resolved is unchanged.
          {
            values: t.map(m => m.addressable),
            config: {
              label: 'Raised — addressable',
              backgroundColor: '#A5A5A6',
              type: 'bar',
              yAxis: 'left',
              offset: 0,
              tooltip: { formatter: d => this.raisedTooltip(d) },
            },
          },
          {
            values: t.map(m => m.blocked),
            config: {
              label: 'Raised — not solvable',
              backgroundColor: '#DDDDDD',
              type: 'bar',
              yAxis: 'left',
              offset: 0,
              tooltip: { formatter: d => this.raisedTooltip(d) },
            },
          },
          {
            values: t.map(m => m.resolved),
            config: {
              label: 'Resolved',
              backgroundColor: '#389A6C',
              type: 'bar',
              yAxis: 'left',
              offset: 1,
            },
          },
          {
            values: t.map(m => m.open),
            config: {
              label: 'Still open',
              backgroundColor: '#F76734',
              type: 'line',
              yAxis: 'right',
              offset: 0,
            },
          },
        ],
      };
    },
    trendConfig() {
      return {
        chartType: 'vertical_bar',
        margin: { top: 40, right: 60, bottom: 50, left: 55 },
        xAxis: { labelRotation: -45, fontSize: 10 },
        yAxes: [
          { position: 'left', min: 0, fontSize: 10 },
          { position: 'right', min: 0, fontSize: 10 },
        ],
        legend: { display: true, position: 'top', spacing: 15 },
        formatting: { percentage: false, currency: false, sig_figs: 0 },
        // Two side-by-side series put the labels a half-bandwidth apart, close enough
        // that adjacent numbers merge into an unreadable run. The values are on the
        // axis and in the tooltip.
        hideDataLabels: true,
        labelDisplayConfig: { minLabelSpacing: 1, labelFontSize: 10 },
      };
    },
    trendTotals() {
      const sum = k => this.trend.reduce((a, m) => a + (m[k] || 0), 0);
      return {
        raised: sum('raised'),
        blocked: sum('blocked'),
        resolved: sum('resolved'),
        open: this.trend.length ? this.trend[this.trend.length - 1].open : 0,
      };
    },
    filtered() {
      const q = this.search.trim().toLowerCase();
      let out = this.gaps.filter(g => {
        if (this.filterPractice && g.practice_name !== this.filterPractice) return false;
        if (this.filterYear && g.study_year !== this.filterYear) return false;
        if (this.filterInitiative && g.initiative !== this.filterInitiative) return false;
        if (this.filterPtm && (g.ptm_name || UNASSIGNED) !== this.filterPtm) return false;
        if (this.filterType && g.input_type !== this.filterType) return false;
        if (this.filterStatus && this.statusKey(g) !== this.filterStatus) return false;
        if (!q) return true;
        return [g.practice_name, g.provider_name, g.ptm_name, g.title, g.npi, g.input_type]
          .some(v => (v || '').toString().toLowerCase().includes(q));
      });
      const k = this.sortKey;
      out = [...out].sort((a, b) => {
        const av = k === 'status' ? this.statusLabel(a) : a[k];
        const bv = k === 'status' ? this.statusLabel(b) : b[k];
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === 'number' && typeof bv === 'number') return av - bv;
        return String(av).localeCompare(String(bv));
      });
      return this.sortDesc ? out.reverse() : out;
    },
  },
  mounted() {
    if (this.canView) this.fetch();
  },
  methods: {
    isSelected(g) {
      return this.selected
        && this.selected.study_year === g.study_year
        && this.selected.entity_id === g.entity_id
        && this.selected.gap_ix === g.gap_ix;
    },
    openDrawer(g) {
      this.selected = g;
    },
    evidenceFor(node) {
      return (this.selected?.evidence || []).filter(e => e.node === node);
    },
    titleCase(s) {
      if (!s) return '';
      return s.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    },
    fmt(v) {
      return v == null ? '—' : `${v.toFixed(1)}%`;
    },
    // Both raised segments share this, so hovering either shows the full split rather
    // than only the piece under the cursor.
    raisedTooltip(d) {
      const m = this.trend.find(x => x.label === d.label);
      if (!m) return '';
      const pct = m.raised ? Math.round((100 * m.blocked) / m.raised) : 0;
      return `<strong>${m.label}</strong><br/>`
        + `${m.raised} raised<br/>`
        + `${m.addressable} addressable<br/>`
        + `${m.blocked} not solvable by a PTM (${pct}%)`;
    },
    statusKey(g) {
      if (g.supplied) return 'resolved';
      if (!g.removable) return 'not_removable';
      if (g.n_interventions > 0) return 'in_progress';
      return 'open';
    },
    statusLabel(g) {
      return {
        resolved: 'Resolved',
        not_removable: 'Not removable',
        in_progress: 'In progress',
        open: 'Open',
      }[this.statusKey(g)];
    },
    statusCls(g) {
      return {
        resolved: 'bg-green-100 text-green-700',
        not_removable: 'bg-gray-100 text-gray-500',
        in_progress: 'bg-amber-100 text-amber-700',
        open: 'bg-red-100 text-red-700',
      }[this.statusKey(g)];
    },
    sortBy(key) {
      if (this.sortKey === key) this.sortDesc = !this.sortDesc;
      else { this.sortKey = key; this.sortDesc = false; }
    },
    clearFilters() {
      this.filterPractice = null;
      this.filterPtm = null;
      this.filterInitiative = null;
      this.filterYear = null;
      this.filterType = null;
      this.filterStatus = null;
      this.search = '';
    },
    exportRows() {
      return this.filtered.map(g => ({
        Year: g.study_year,
        Practice: this.titleCase(g.practice_name),
        Provider: this.titleCase(g.provider_name),
        NPI: g.npi,
        PTM: g.ptm_name || '',
        Initiative: g.initiative.toUpperCase(),
        Type: g.input_type || '',
        Constraint: g.title,
        Detail: g.summary || '',
        Status: this.statusLabel(g),
        'First Raised': g.first_stated_date || '',
        Resolved: g.supplied_date || '',
        Interventions: g.n_interventions ?? 0,
        'Vytalize Acted': g.has_vytalize_intervention ? 'Yes' : 'No',
        'Practice Acted': g.has_practice_intervention ? 'Yes' : 'No',
        Before: g.pre_value == null ? '' : Number(g.pre_value.toFixed(2)),
        After: g.post_value == null ? '' : Number(g.post_value.toFixed(2)),
        Change: g.delta == null ? '' : Number(g.delta.toFixed(2)),
        'Not Removable Reason': g.removable ? '' : (g.not_removable_reason || ''),
      }));
    },
    exportXlsx() {
      const rows = this.exportRows();
      if (!rows.length) return;
      const today = new Date().toISOString().split('T')[0];
      exportToExcel(rows, `performance_signal_study_${today}.xlsx`, { sheetName: 'Constraints' });
    },
    exportCsv() {
      const rows = this.exportRows();
      if (!rows.length) return;
      const cols = Object.keys(rows[0]);
      const esc = v => {
        const str = v == null ? '' : String(v);
        return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
      };
      const csv = [cols.join(','), ...rows.map(r => cols.map(c => esc(r[c])).join(','))].join('\n');
      const today = new Date().toISOString().split('T')[0];
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `performance_signal_study_${today}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    async fetch() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get('api/impact_analytics/signal-study/');
        this.gaps = data.gaps || [];
      } catch (err) {
        console.error('Error loading signal study:', err);
        this.error = 'Failed to load';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
